// Settings (#settings, DC-32 — every seat): the person's own preferences. In B1 that is the theme (System · Light ·
// Dark, the same choice the navigation's Theme select writes; the seat default of Q-E2 is not applied), and for the
// seats that hold them, the way to the screens the navigation does not list: Phase targets, the configuration console
// and V1's Admin system screens. A V2 view: App.tsx renders it inside its own `.mc` island.

import { useEffect, useState } from "react";
import { configList } from "@/api/tools";
import { useRead } from "@/api/useTool";
import { Card, Stack, TextLink } from "@/mc/core";
import { Segmented } from "@/mc/g1";
import { PageHeader, SHELL_PAGE_LABELS } from "@/mc/g4a";
import { NAV_ITEM_LABELS } from "@/mc/labels";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { chooseTheme, themePreference, type ThemeChoice } from "@/shell/theme";

const THEMES: readonly { value: ThemeChoice; label: string; hs: string }[] = [
  { value: "system", label: "System", hs: "settings.theme.system" },
  { value: "light", label: "Light", hs: "settings.theme.light" },
  { value: "dark", label: "Dark", hs: "settings.theme.dark" },
];

/** The read that says whether the seat's own domain holds configuration: config.list returns only that domain's kinds. */
export const CONSOLE_READ = ["config", "settings-console"] as const;

export function Settings() {
  const { can } = useSession();
  const { register } = useViewState();
  const [theme, setTheme] = useState(themePreference);
  useEffect(() => register({ view: "settings", ids: {}, summary: { tab: "theme" } }), [register]);
  useEffect(() => {
    const sync = () => setTheme(themePreference());
    window.addEventListener("ta:theme", sync);
    return () => window.removeEventListener("ta:theme", sync);
  }, []);
  // Each link shows only where the manifest admits its screen's tool, never by role, and names where it goes. Phase
  // targets is the phase report's read (requisition.list, its route's gate). The configuration console is linked only
  // when config.list returns a version: the server lists the kinds of the seat's own domain, and a domain that holds no
  // configuration kind would open an empty console (since DC-228 the one Admin holds every domain). The system screens
  // are the Admin's Feedback, Outbox, Audit and System tabs.
  const reportsPhases = can("requisition.list");
  const configures = can("config.list");
  const held = useRead(CONSOLE_READ, () => configList({ limit: 1 }), { enabled: configures });
  const opensConsole = configures && (held.data?.rows.length ?? 0) > 0;
  const administers = can("feedback.list") || can("admin.system");
  return (
    <Stack>
      <PageHeader title={NAV_ITEM_LABELS.settings} />
      <Card title="Theme">
        <Segmented<ThemeChoice> label="Theme" value={theme} options={THEMES} onChange={(choice) => chooseTheme(choice)} />
      </Card>
      {reportsPhases || opensConsole || administers ? (
        <Card>
          <Stack>
            <TextLink href="#phase-targets" can={reportsPhases} after="right" >Phase targets</TextLink>
            <TextLink href="#admin/config" can={opensConsole} after="right" >{SHELL_PAGE_LABELS.configurationConsole}</TextLink>
            <TextLink href="#admin/feedback" can={administers} after="right" >Feedback, Outbox, Audit and System</TextLink>
          </Stack>
        </Card>
      ) : null}
    </Stack>
  );
}
